<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";

interface ProcessStep {
  number?: string;
  title: string;
  desc: string;
  rawDesc?: string;
}

const props = withDefaults(
  defineProps<{
    heading?: string;
    subcopy?: string;
    steps?: ProcessStep[];
    bgImageLight?: string;
    bgImageDark?: string;
    ctaText?: string;
    ctaLink?: string | null;
  }>(),
  {
    heading: "",
    subcopy: "",
    steps: () => [],
    bgImageLight: "/images/our-logo-design-process-light.svg",
    bgImageDark: "/images/our-logo-design-process-dark.svg",
    ctaText: "",
    ctaLink: null,
  },
);

const emit = defineEmits<{
  'cta-clicked': [];
}>();

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

const sectionSubtitleColor = computed(() =>
  colorMode.preference === "dark" ? "text-[#D9D9D9]" : "text-[#000000]",
);

const cardBorderStyle = computed(() =>
  colorMode.value === "dark"
    ? "border border-[#3D1B60] bg-[linear-gradient(136deg,rgba(1,3,20,0.40)_6.22%,rgba(43,1,82,0.40)_93.78%)]"
    : "border border-[rgba(208,208,208,0.50)] bg-white shadow-[0_4px_8px_0_rgba(0,0,0,0.04)]",
);

/* Staggered layout: side cards (03, 04) and center cards (01, 02) */
const sideCardStyle = computed(() =>
  isLight.value
    ? {
        borderRadius: "15.167px",
        background: "#FFF",
        boxShadow:
          "-13.72px 0 19.64px 0 rgba(255, 255, 255, 0.03) inset, 13.72px 0 19.64px 0 rgba(255, 255, 255, 0.03) inset",
        backdropFilter: "blur(14.26px)",
      }
    : {
        background: "rgba(113, 6, 253, 0.22)",
        boxShadow:
          "-13.72px 0 19.64px 0 rgba(255, 255, 255, 0.03) inset, 13.72px 0 19.64px 0 rgba(255, 255, 255, 0.03) inset",
        backdropFilter: "blur(14.26px)",
      },
);

const centerCardStyle = computed(() =>
  isLight.value
    ? {
        borderRadius: "15.167px",
        background: "linear-gradient(206deg, #9748FF 1.7%, #7C2BFF 110.46%)",
        backdropFilter: "blur(149px)",
      }
    : {
        background: "linear-gradient(206deg, #7031C2 1.7%, #2C0073 110.46%)",
        backdropFilter: "blur(149px)",
      },
);

/* Display steps in visual order: 03, 01, 02, 04 */
const displaySteps = computed(() => {
  const s = props.steps;
  if (s.length < 4) return [];
  return [
    { ...s[2], number: s[2].number ?? "03" },
    { ...s[0], number: s[0].number ?? "01" },
    { ...s[1], number: s[1].number ?? "02" },
    { ...s[3], number: s[3].number ?? "04" },
  ];
});
</script>

<template>
  <div class="mt-16 lg:mt-[100px] mb-10 lg:mb-[100px]">
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center"
      style="
        font-weight: 600;
        background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      "
    >
      <span v-html="heading" />
    </h2>
    <p
      v-if="subcopy"
      class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 px-4 lg:opacity-70 text-center max-w-[500px] mx-auto font-avant"
      :class="sectionSubtitleColor"
    >
      {{ subcopy }}
    </p>

    <!-- Process Cards: staggered layout (03, 01, 02, 04) -->
    <div
      class="relative flex flex-wrap justify-center items-center gap-3 sm:gap-4 mt-8 sm:mt-12 px-4"
    >
      <!-- Background decoration SVG -->
      <img
        v-if="bgImageLight && isLight"
        :src="bgImageLight"
        :alt="heading + ' background illustration'"
        class="absolute left-1/2 -translate-x-1/2 top-0 w-full max-w-[1077px] h-auto pointer-events-none lg:block hidden"
      />
      <img
        v-else-if="bgImageDark && !isLight"
        :src="bgImageDark"
        :alt="heading + ' background illustration'"
        class="absolute left-1/2 -translate-x-1/2 top-0 w-full max-w-[1077px] h-auto pointer-events-none lg:block hidden"
      />

      <!-- Staggered cards: di 0=side(03), 1=center(01), 2=center(02), 3=side(04) -->
      <div
        v-for="(step, di) in displaySteps"
        :key="di"
        class="flex flex-col items-start p-3 sm:px-2 sm:p-5 rounded-[15px] relative"
        :class="[
          di === 0 || di === 3
            ? 'w-full sm:w-[180px] lg:w-[180px] min-h-[180px] sm:min-h-[274px]'
            : 'w-full sm:w-[200px] lg:w-[232px] min-h-[180px] sm:min-h-[354px]',
          cardBorderStyle,
          di === 0 || di === 3
            ? isLight
              ? 'process-card-mobile-gradient'
              : 'process-card-mobile-gradient process-card-mobile-gradient-dark'
            : '',
          di === 0 ? 'order-3 sm:order-1' : '',
          di === 1 ? 'order-1 sm:order-2' : '',
          di === 2 ? 'order-2 sm:order-3' : '',
          di === 3 ? 'order-4 sm:order-4' : '',
        ]"
        :style="di === 0 || di === 3 ? sideCardStyle : centerCardStyle"
      >
        <!-- Number -->
        <span
          :class="[
            di === 0 || di === 3
              ? 'text-[36px] sm:text-[36px] lg:text-[53px] font-semibold leading-normal process-card-03-04-number font-avant-demi'
              : 'text-[36px] sm:text-[36px] lg:text-[69px] font-semibold text-[#FFF] leading-normal font-avant-demi',
          ]"
          :style="
            di === 0 || di === 3
              ? {
                  color: isLight ? '#613FAA' : '#FFF',
                  opacity: isLight ? 1 : 0.6,
                }
              : undefined
          "
        >
          {{ step.number }}
        </span>

        <!-- Title -->
        <h3
          :class="[
            di === 0 || di === 3
              ? 'mt-[10px] sm:mt-[30px] text-[18px] sm:text-[14px] lg:text-[17px] font-semibold leading-normal process-card-03-04-heading font-avant-demi'
              : 'mt-[15px] sm:mt-[50px] text-[18px] sm:text-[14px] lg:text-[23px] font-semibold leading-[1.2] self-stretch font-avant-demi',
          ]"
          :style="
            di === 0 || di === 3
              ? {
                  color: isLight ? '#613FAA' : '#A561FF',
                  opacity: isLight ? 1 : 0.6,
                }
              : { color: isLight ? '#FFF' : '#A561FF' }
          "
        >
          {{ step.title }}
        </h3>

        <!-- Description -->
        <p
          :class="[
            di === 0 || di === 3
              ? 'mt-2 text-[15px] sm:text-[11px] lg:text-[11px] leading-normal process-card-03-04-desc font-avant'
              : 'mt-2 text-[15px] sm:text-[11px] lg:text-[16px] text-[#FFF] leading-[1.2] font-avant',
          ]"
          :style="
            di === 0 || di === 3
              ? {
                  color: isLight ? '#000000' : '#FFF',
                  opacity: isLight ? 1 : 0.6,
                }
              : undefined
          "
          v-html="step.rawDesc || step.desc"
        />
      </div>
    </div>

    <!-- CTA Button -->
    <div v-if="ctaText" class="flex justify-center mt-6 sm:mt-10">
      <WrButton
        :link="ctaLink || undefined"
        @click="!ctaLink ? emit('cta-clicked') : undefined"
        container-class="h-[40px] lg:h-[48px]"
        append-class="text-md lg:font-normal px-3 lg:px-5"
        >{{ ctaText }}</WrButton
      >
    </div>
  </div>
</template>

<style scoped>
/* Mobile-only gradient for process cards 03 & 04 */
@media (max-width: 639px) {
  .process-card-mobile-gradient {
    background: linear-gradient(
      206deg,
      #9748ff 1.7%,
      #7c2bff 110.46%
    ) !important;
  }

  .process-card-mobile-gradient-dark {
    background: linear-gradient(
      206deg,
      #7031c2 1.7%,
      #2c0073 110.46%
    ) !important;
  }

  /* Match number/heading/desc color & opacity to cards 01/02 on mobile */
  .process-card-03-04-number {
    color: #fff !important;
    opacity: 1 !important;
  }

  .process-card-03-04-heading {
    color: #fff !important;
    opacity: 1 !important;
  }

  .process-card-03-04-desc {
    color: #fff !important;
    opacity: 1 !important;
  }

  /* Dark mode: heading matches card 01/02 (#A561FF) */
  .process-card-mobile-gradient-dark .process-card-03-04-heading {
    color: #a561ff !important;
  }
}
</style>
